import { usePage } from '@inertiajs/react';
import { ReactNode } from 'react';
import { ChevronRight } from 'react-feather';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { setServerErrors, useMutation } from '../../Shared/api';
import { useNotifier } from '../../Shared/Notifier/NotificationProvider';
import { Button } from '../../Shared/UI/Button';
import { Card } from '../../Shared/UI/Card';
import ErrorMessage from '../../Shared/UI/ErrorMessage';
import { Icon } from '../../Shared/UI/Icon';
import { Input } from '../../Shared/UI/Input';
import InputGroup from '../../Shared/UI/InputGroup';
import InputLabel from '../../Shared/UI/InputLabel';
import ServerErrors from '../../Shared/UI/ServerErrors';
import { useRouter } from '../router';
import { PageProps } from '../types';
import Container from '../UI/Container';
import Layout from '../UI/Layout';
import RegistrationProgress from '../UI/RegistrationProgress';

export default function AddCharityPage() {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();
  const { locale } = usePage<PageProps>().props;

  const { register, handleSubmit, setError, formState: { errors, defaultValues } } = useForm({
    defaultValues: {
      title: '',
      locale,
    },
  });

  const [addCharity, { loading, errors: serverErrors }] = useMutation(routes.add_charity(), {
    onSuccess: () => {
      notifier.notify(t('backend:charity_created'));
    },
    onError: (errors) => {
      setServerErrors(errors, setError, defaultValues);
    },
  });

  return (
    <>
      <RegistrationProgress step="create_charity" />
      <Container size="sm">
        <Card>
          <form onSubmit={handleSubmit(addCharity)}>
            <div className="space-y-6">
              <div>
                <h2 className="mb-1">
                  {t('backend:add_charity_title')}
                </h2>
                <p className="text-slate-700">
                  {t('backend:add_charity_intro')}
                </p>
              </div>

              {serverErrors && <ServerErrors errors={serverErrors} defaultValues={defaultValues} scrollIntoView />}

              <InputGroup>
                <InputLabel required valid={!errors.title} htmlFor="add_charity_title">
                  {t('backend:organisation_name')}
                </InputLabel>
                <Input
                  {...register('title', { required: true })}
                  autoFocus
                  id="add_charity_title"
                  aria-invalid={!!errors.title}
                />
                <ErrorMessage attribute={t('backend:organisation_name')} error={errors.title }/>
              </InputGroup>

              <Button
                type="submit"
                disabled={loading}
                className="block w-full"
                loading={loading}
              >
                {t('backend:continue')}
                <Icon className="ml-1">
                  <ChevronRight />
                </Icon>
              </Button>
            </div>
          </form>
        </Card>
      </Container>
    </>
  );
}

AddCharityPage.layout = (page: ReactNode) => <Layout>{page}</Layout>;
